/// <amd-module name="Search/Code/Components/LeftPane" />

import * as React from "react";
import * as Container from "Search/Shared/Base/Container";
import * as Constants from "Search/Code/Constants";
import * as Resources from "Search/Code/Resources";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { format } from "VSS/Core/Util/String";
import { SearchStatus } from "Search/Shared/Base/Stores/SearchStore";
import { ItemsList, IItemsListProps } from "Search/Code/Components/ItemsList";
import { Intersection } from "VSSUI/Intersection";
import { ShowMoreRow } from "Search/Shared/Components/ShowMoreRow";
import { ResultsInfo } from "Search/Shared/Components/ResultsInfo";
import { ActionCreator } from "Search/Code/Flux/ActionCreator";
import { StoresHub, IAggregatedState, StoreName } from "Search/Code/Flux/StoresHub"
import { CodeResult, SearchQuery, VersionControlType } from "Search/LegacyWebApi/Contracts/Code";
import { LoadMore } from "Search/Shared/Components/LoadMore";
import { isGitType, isTfvcType, isCustomType } from "../Utils";


export interface ILeftPaneContainerProps extends Container.IContainerProps<StoreName, ActionCreator, IAggregatedState, StoresHub> {
    previewOrientation: string;
}

interface IScrollableItemsListProps extends IItemsListProps {
    infiniteScroll: boolean;

    moreResultsAvailable: boolean;

    fetchMoreResults: (newSkip: number, newTake: number) => void;

    query: SearchQuery;
}

export const LeftPaneContainer = Container.create<StoreName, ActionCreator, IAggregatedState, StoresHub, ILeftPaneContainerProps>(
    [
        "searchStore",
        "itemContentStore",
    ],
    ({
        searchStoreState,
        itemContentStoreState
    }, props) => {
        const { query, response, searchStatus, activityId, fetchMoreScenario, CommitId, LastProcessedTime, Commit } = searchStoreState;
        const {        
            clickFeedbackLink,
            fetchMoreResults,
            notifyResultsRendered,
            changeActiveItem,
            clickLeftPaneFileNameLink,
            clickContextualMenuItem,        
        } = props.actionCreator;
        const items = response ? response.results.values : [];
        const totalResultsCount = response ? response.results.count : 0;
        const searchSucceeded = searchStatus === SearchStatus.Success;
        const needShowMore = response
            ? (Math.min(totalResultsCount, Constants.CodeSearchMaxSkipOrTakeResults) > (query.skipResults + query.takeResults))
            : false;

        const infiniteScroll = isFeatureFlagEnabled(props.pageContext, Constants.FeatureFlagConstants.InfiniteScroll, false);
        const dataRefreshInfo = isFeatureFlagEnabled(props.pageContext, Constants.FeatureFlagConstants.DataRefreshInfo, false);
        // Add an empty row to the items to render spinner while fetching the results in case of infinite scroll.
        const newItems = infiniteScroll && needShowMore ? [...items, {} as CodeResult] : items;
        
        if (searchSucceeded || fetchMoreScenario) {
            const mail = format(Constants.CodexFeedbackMailToLinkFormat, activityId);
            const activeItemIndex = items.indexOf(itemContentStoreState.selectedItem);
            const showDataRefreshLink = query.searchFilters[Constants.FilterKeys.RepositoryFiltersKey] && query.searchFilters[Constants.FilterKeys.RepositoryFiltersKey].length === 1 ? true : false;
            return (
                <>
                    {
                        items.length > 0 &&
                        <ResultsInfo
                            infoMessage={getResultsInfoMessage(items.length, totalResultsCount)}
                            mailToLink={mail}
                            showFeedbackLink={dataRefreshInfo && props.isHosted && showDataRefreshLink && (isTfvcType(items[activeItemIndex].vcType) || isGitType(items[activeItemIndex].vcType) || isCustomType(items[activeItemIndex].vcType))}
                            onFeedbackLinkInvoked={clickFeedbackLink}
                            stalenessMessage={getRefreshInfoText(items[activeItemIndex].vcType, LastProcessedTime, CommitId, Commit)}
                        />
                    }
                    <ScrollableItemsList
                        items={newItems}
                        activeItemIndex={activeItemIndex}
                        itemsLoading={searchStatus === SearchStatus.Loading}
                        notifyOnRendered={notifyResultsRendered}
                        onItemActivation={changeActiveItem}
                        onItemFileNameClicked={clickLeftPaneFileNameLink}
                        onItemMenuInvoked={clickContextualMenuItem}
                        pageContext={props.pageContext}
                        previewOrientation={props.previewOrientation}
                        renderLoadingRow={() => getLoadMoreRow(searchStatus === SearchStatus.Failed)}
                        infiniteScroll={infiniteScroll}
                        moreResultsAvailable={needShowMore}
                        fetchMoreResults={fetchMoreResults}
                        query={query} />
                </>);
        }

        return null;
    });

export class ScrollableItemsList extends React.Component<IScrollableItemsListProps, {}> {
    private listRef: React.RefObject<HTMLDivElement | null> = React.createRef<HTMLDivElement>();
    private timerManagement: TimerManagement;

    constructor(props: IScrollableItemsListProps) {
        super(props);
        this.timerManagement = new TimerManagement();
    }
    public render(): JSX.Element {
        const { items, itemsLoading, infiniteScroll, fetchMoreResults, moreResultsAvailable, query } = this.props;
        return (
            <Intersection>
                <div
                    // @ts-ignore TypeScript 3.5 update
                    ref={this.listRef}
                    className="flex-grow v-scroll-auto h-scroll-hidden">
                    {
                        items.length > 0 &&
                        <ItemsList {...this.props} />
                    }
                    {
                        !infiniteScroll &&
                        <ShowMoreRow
                            className="code-search-cell flex-noshrink"
                            onClick={() => fetchMoreResults(query.takeResults, Constants.CodeSearchMaxSkipOrTakeResults)}
                            needShowMore={moreResultsAvailable}
                            itemsLoading={itemsLoading} />
                    }
                </div>
            </Intersection>
        );
    }

    public componentDidMount(): void {
        if (this.listRef.current) {
            this.listRef.current.addEventListener("scroll", this.fetchMoreIfNeeded);
            this.timerManagement.setTimeout(() => {
                this.fetchMoreIfNeeded();
            }, 0);
        }
    }

  

    public componentWillUnmount(): void {
        this.timerManagement.dispose();
        if (this.listRef.current) {
            this.listRef.current.removeEventListener("scroll", this.fetchMoreIfNeeded);
        }
    }

    private fetchMoreIfNeeded = (): void => {
        const { query, fetchMoreResults, moreResultsAvailable, infiniteScroll } = this.props;
        if (this.listRef && this.listRef.current && infiniteScroll && moreResultsAvailable) {
            const viewPortBottom = this.listRef.current.clientHeight + this.listRef.current.scrollTop;
            const totalHeight = this.listRef.current.scrollHeight;

            if (totalHeight - viewPortBottom < 150) {
                const newSkip = Math.min(query.skipResults + query.takeResults, Constants.CodeSearchMaxSkipOrTakeResults);
                const newTake = Constants.CodeSearchLoadMoreTakeResults;
                fetchMoreResults(newSkip, newTake);
            }
        }
    }
}

function getResultsInfoMessage(resultsCount: number, totalCount: number): string {
    
    return resultsCount < totalCount
        ? format(Resources.ShowingCodeResultsFormat, resultsCount, totalCount)
        : (resultsCount === 1
            ? Resources.ShowingSingleCodeResult
            : format(Resources.ShowingNCodeResultsFormat, totalCount));
}

function getRefreshInfoText(vcType: VersionControlType, LastProcessedTime?: Date, CommitId?: string, Commit?: Date): string {
    if (isGitType(vcType))
        return !Commit && !CommitId ? Resources.DataRefreshInfoTooltip : format(Resources.DataRefreshInfoMessageGit, CommitId?.substring(0, 8), Commit?.toLocaleString(), LastProcessedTime?.toLocaleString());
    else if (isTfvcType(vcType))
        return !Commit && !CommitId ? Resources.DataRefreshInfoTooltip : format(Resources.DataRefreshInfoMessageTFVC, CommitId?.substring(0, 8), Commit?.toLocaleString(), LastProcessedTime?.toLocaleString());
    else (isCustomType(vcType))
    {
        if (CommitId && parseInt(CommitId) > 0)
            return !Commit && !CommitId ? Resources.DataRefreshInfoTooltip : format(Resources.DataRefreshInfoMessageCustom, CommitId?.toLocaleString(), Commit?.toLocaleString(), LastProcessedTime?.toLocaleString());
        else
            return !Commit && !CommitId ? Resources.DataRefreshInfoTooltip : format(Resources.DataRefreshInfoMessageCustomNotAvailable);
    }
}

function getLoadMoreRow(loadMoreFailed: boolean): JSX.Element {
    return <LoadMore
        error={loadMoreFailed} />;
}